// @flow

import * as React from 'react';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import {
    defineMessages, FormattedMessage, injectIntl, IntlShape,
} from 'react-intl';
import { connectAPI, withConfirmation, withRouter } from 'speed-js-react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import DeleteIcon from 'esm-web-pro/components/layout/sidePanel/icons/DeleteIcon';
import PlusIcon from 'esm-web-pro/components/layout/sidePanel/icons/Plus';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import { ApiClient } from 'speed-js-core';
import { get } from 'lodash';
import { Box } from 'grommet';
import type { Confirmation } from 'speed-js-react';
import NotificationTypeContent from 'esm-web-pro/containers/notifications/NotificationTypeContent';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import sidePanelMessages from 'esm-web-pro/messages/sidePanelMessages';
import NotificationTypeAddContent from './NotificationTypeAddContent';

type SidePanelActionProps = {
    openPanel: (key: string, url: ?string, sensitivity?: boolean) => { key: string, url: string },
    closePanel: (key: string) => { key: string },
    setSensitivity: (key: string, sensitivity: boolean) => void,
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    confirm: (confirmation: Confirmation) => void,
    getObject: (objectId: string, resourceName: string) => Promise<Object>,
    intl: IntlShape,
    history: Object,
    redirectTo: string,
    resourceName: string,
    deleteObject?: (objectId: string, resourceName: string) => Promise<Object>,
    onClose: Function,
    match: Object
}

const NOTIFICATION_TYPE_PAGE_KEY = 'NOTIFICATION_TYPE_PAGE';
const ADD_CONTENT_KEY = 'ADD_CONTENT';

const mapDispatchToProps = (dispatch) => (bindActionCreators(
    sidePanelActions, dispatch,
));

const msg = defineMessages({
    title: {
        id: 'notification-type-panel-title',
        defaultMessage: 'Notification Type',
    },
    cancelDelete: {
        id: 'notification-type-cancel-delete',
        defaultMessage: 'Ok',
    },
    cannotDelete: {
        id: 'notification-type-cannot-delete',
        defaultMessage: 'Cannot delete object as there are related objects to it.',
    },
    deleteNotificationType: {
        id: 'notification-type-delete',
        defaultMessage: 'Are you sure you want to delete the notification type?.',
    },
});

const NotificationTypePage = (props: Props) => {
    const {
        match, intl, history, resourceName, deleteObject, confirm, openPanel, getObject, setSensitivity,
    } = props;

    const [object, setObject] = React.useState(null);
    const objectId = match.params.id;

    React.useEffect(() => {
        getObject(objectId, resourceName).then((res) => setObject(res));
    }, []);

    const handleClose = (skipSensitivity = false) => {
        const {
            redirectTo, onClose, closePanel,
        } = props;


        if (skipSensitivity) {
            setSensitivity(NOTIFICATION_TYPE_PAGE_KEY, false);
        }

        history.push(redirectTo);
        closePanel(NOTIFICATION_TYPE_PAGE_KEY, skipSensitivity);
        if (onClose) {
            onClose();
        }
    };

    const invokeConfirmDialog = React.useCallback((message, confirmCallback) => confirm({
        message,
        actions: {
            cancel: {
                label: <FormattedMessage id="cancel-btn" defaultMessage="Cancel" />,
                callback: () => {
                },
                type: 'negative',
            },
            confirm: {
                label: <FormattedMessage id="confirm-btn" defaultMessage="Confirm" />,
                callback: confirmCallback,
                type: 'positive',
            },
        },
    }), [confirm]);

    const deleteNotificationTypeCallback = () => {
        if (deleteObject) {
            deleteObject(objectId, resourceName)
                .then((res) => {
                    if (res.data && res.data.detail) {
                        confirm({
                            message: intl.formatMessage(msg.cannotDelete),
                            actions: {
                                cancel: {
                                    label: intl.formatMessage(msg.cancelDelete),
                                    callback: () => {
                                    },
                                    type: 'negative',
                                },
                            },
                        });
                    } else {
                        handleClose();
                        history.push(match.url.split('info')[0]);
                    }
                });
        }
    };

    const actions = [
        {
            key: 'notification-type-edit',
            icon: <EditIcon />,
            callback: () => {
                const path = match.url.split('info')[0];
                history.push(`${path}update/${objectId}`);
            },
            tooltip: intl.formatMessage(actionMessages.edit),
            color: 'transparent',
            marginVertical: '15px',
            radius: '0%',
        },
        {
            key: 'notification-type-delete',
            icon: <DeleteIcon />,
            callback: () => invokeConfirmDialog(
                intl.formatMessage(msg.deleteNotificationType), deleteNotificationTypeCallback,
            ),
            tooltip: intl.formatMessage(actionMessages.delete),
            color: 'transparent',
            marginVertical: '15px',
            radius: '0%',
        },
        {
            key: 'notification-type-add',
            icon: <PlusIcon />,
            callback: () => openPanel(
                ADD_CONTENT_KEY,
                intl.formatMessage(sidePanelMessages.addContent),
                match.url,
                true,
            ),
            tooltip: intl.formatMessage(actionMessages.addContent),
            color: 'transparent',
            marginVertical: '15px',
            radius: '0%',
        },
    ];

    return (
        <>
            <SidePane
                id={NOTIFICATION_TYPE_PAGE_KEY}
                url={match.url}
                openInitially
                closeAllOpened
                key={objectId}
                onClose={handleClose}
                title={`${object?.translations[intl.locale].name}`}
            >
                <Box pad={{ horizontal: 'small' }}>
                    <Box direction="row">
                        <MainActions main actions={actions} />
                    </Box>
                    <HorizontalLine color="grey" />
                </Box>
                <BottomContainer>
                    { object && <NotificationTypeContent notification={object} /> }
                </BottomContainer>
            </SidePane>
            <SidePane
                id={ADD_CONTENT_KEY}
                key={`add-content-${objectId}`}
                title={intl.formatMessage(sidePanelMessages.addContent)}
            >
                <NotificationTypeAddContent
                    onClose={handleClose}
                    object={object}
                    resourceName={resourceName}
                />
            </SidePane>
        </>
    );
};

NotificationTypePage.defaultProps = {
    deleteObject: null,
};

const apiCallback = (apiClient: ApiClient) => ({
    deleteObject: {
        send: (id, resourceName, resourceArguments = []) => {
            const resource = get(apiClient.resources, resourceName);
            return resource.delete(...resourceArguments, id);
        },
    },
    getObject: {
        send: (id, resourceName, resourceArguments = []) => {
            const resource = get(apiClient.resources, resourceName);
            return resource.get(...resourceArguments, id);
        },
    },
});

export default connectAPI(apiCallback)(
    withRouter(
        withConfirmation(
            injectIntl(
                connect(null, mapDispatchToProps)(NotificationTypePage),
            ),
        ),
    ),
);
